import React, {useEffect} from 'react';
import formStyles from "../../LocationForm.module.css";
import styles from "./SupportedManufacturersField.module.css";
import {LocationRequestPartial} from "../../LocationForm";
import {RootState} from "../../../../../../../app/store";
import {useAppDispatch, useAppSelector} from "../../../../../../../app/hooks";
import {RequestStatus} from "../../../../../../../api/apiConfiguration";
import {NewVehicleDataConfigurationDTOVehicleTypeEnum} from "../../../../../../../api/generated";
import {getVehicleTypeTitle} from "../../../../../../../utils/enumValueTitleUtil";
import {useRequestErrorToast} from "../../../../../../../hooks/useRequestErrorToast";
import {MultiSelectDropdown} from "../../../../../../elements/multiSelect/MultiSelectDropdown";
import {WhiteTooltip} from "../../../../../../elements/tooltip/WhiteTooltip";
import {
    catalogManufacturersSelector,
    fetchManufacturerVehicleTypes,
    manufacturerVehicleTypesRequestStatusSelector,
    manufacturerVehicleTypesSelector,
    VehicleTypesByManufacturer
} from "../../../../manufacturerVehicleTypesSlice";

type VehicleType = NewVehicleDataConfigurationDTOVehicleTypeEnum;

interface Props {
    updateRequest: (update: LocationRequestPartial) => void;
    supportedVehicleManufacturersSelector: (state: RootState) => VehicleTypesByManufacturer | undefined;
}

export const SupportedManufacturersField = ({updateRequest, supportedVehicleManufacturersSelector}: Props) => {
    const dispatch = useAppDispatch();
    const supported = useAppSelector(supportedVehicleManufacturersSelector) || {};
    const catalog = useAppSelector(manufacturerVehicleTypesSelector);
    const catalogManufacturers = useAppSelector(catalogManufacturersSelector);
    const catalogStatus = useAppSelector(manufacturerVehicleTypesRequestStatusSelector);
    const catalogLoaded = catalogStatus === RequestStatus.RESOLVED;

    useEffect(() => {
        dispatch(fetchManufacturerVehicleTypes());
    }, [dispatch]);

    useRequestErrorToast(catalogStatus, "Failed to load the manufacturer catalog");

    const selectedManufacturers = Object.keys(supported).sort((a, b) => a.localeCompare(b));
    const manufacturerOptions = [...catalogManufacturers, ...selectedManufacturers.filter(make => !catalog[make])]
        .map(make => ({label: make, value: make}));

    const selectManufacturers = (makes: string[]) =>
        updateRequest({
            supportedVehicleManufacturers: Object.fromEntries(
                makes.map(make => [make, supported[make] ?? catalog[make] ?? []]))
        });

    const selectVehicleTypes = (make: string, vehicleTypes: VehicleType[]) => {
        const {[make]: _removed, ...others} = supported;
        updateRequest({
            supportedVehicleManufacturers: vehicleTypes.length > 0 ? {...supported, [make]: vehicleTypes} : others
        });
    };

    return <div className={styles.wrapper}>
        <div className={formStyles.fieldsLineWithMargin}>
            <MultiSelectDropdown<string>
                title={'Supported Manufacturers'}
                titleAccessory={<WhiteTooltip title={<span className={formStyles.fieldTooltipText}>
                    If no manufacturers are selected, this location accepts all vehicles.
                    Otherwise it services only the selected manufacturers, for the vehicle types chosen for each.
                </span>}>
                    <span className={formStyles.fieldTooltipIcon} aria-label="Supported manufacturers help" tabIndex={0}>?</span>
                </WhiteTooltip>}
                value={selectedManufacturers}
                handleChange={selectManufacturers}
                options={manufacturerOptions}
                disabled={!catalogLoaded}
                isEqual={(a, b) => a === b}
            />
        </div>
        {catalogLoaded && selectedManufacturers.map(make =>
            catalog[make]
                ? <div key={make} className={formStyles.fieldsLineWithMargin}>
                    <MultiSelectDropdown<VehicleType>
                        title={`${make} Vehicle Types`}
                        value={supported[make]}
                        handleChange={vehicleTypes => selectVehicleTypes(make, vehicleTypes)}
                        options={catalog[make].map(vehicleType => ({label: getVehicleTypeTitle(vehicleType), value: vehicleType}))}
                        isEqual={(a, b) => a === b}
                    />
                </div>
                : <div key={make} className={styles.unknownManufacturer}>
                    {make}: all vehicle types. This manufacturer is not in the catalog, so its types can't be chosen.
                </div>
        )}
    </div>;
};
